import { useState } from 'react'
import { Space, Typography, Tooltip, Row, Col, Form, Modal as ModalAntd, notification } from 'antd'
import { SettingOutlined, DeleteOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { useAsync } from 'react-use'
import _ from 'lodash'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Input from 'components/Input'
import TextArea from 'components/TextArea'
import Select from 'components/Select'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import {
  getGroupAttributeListApi,
  createGroupAttributeApi,
  deleteGroupAttributeByIdApi,
  updateGroupAttributeByIdApi,
} from 'services/groupAttribute'

import Styled from './styled'

export default function PageGroupAttribute () {
  const { t } = useTranslate()

  const [form] = Form.useForm()

  const [dataSource, setDataSource] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    param: null,
  })
  const [status, setStatus] = useState('all') // all, active, inactive
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const _list = async () => {
    if (loading) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getGroupAttributeListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setDataSource(res.data)
  }

  const _create = async (values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await createGroupAttributeApi({
      attribute_group_name: values?.name,
      attribute_group_description: values?.description,
      attribute_group_order: _.size(dataSource),
      is_active: values?.isEnabled,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _update = async (id, values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await updateGroupAttributeByIdApi({
      id,
      attribute_group_name: values?.name,
      attribute_group_description: values?.description,
      attribute_group_order: values?.order,
      is_active: values?.isEnabled,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _delete = async (id) => {
    if (deleting) return

    setDeleting(true)

    const res = await deleteGroupAttributeByIdApi(id)

    setDeleting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()
  }

  const _reorder = async (items) => {
    let isReorder = false

    await Promise.all(
      _.map(items, async (item, index) => {
        if (item.order !== index) {
          isReorder = true

          const id = item.id

          const res = await updateGroupAttributeByIdApi({
            id,
            attribute_group_name: item?.name,
            attribute_group_description: item?.description,
            attribute_group_order: index,
            is_active: item?.isEnabled,
          })

          if (res.status === 'error') {
            notification.error({
              message: 'Failed',
              description: res.message,
            })

            return
          }
        }
      })
    )

    if (isReorder) {
      await _list()
    }

    return isReorder
  }

  useAsync(async () => {
    _list()
  }, [status, filters])

  useAsync(async () => {
    await _reorder(dataSource)
  }, [dataSource])

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      className: 'bold',
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('Order'),
      dataIndex: 'order',
      key: 'order',
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      render: (text, record, index) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={() => {
                  setEditingId(record?.id)

                  const editingDataSource = _.find(dataSource, { id: record?.id })

                  form.setFieldsValue({
                    name: editingDataSource.name,
                    description: editingDataSource.description,
                    order: editingDataSource.order,
                    isEnabled: editingDataSource.isEnabled,
                  })

                  setShowCreateModal(true)
                }}
              />
            </Tooltip>
            <Tooltip title={t('Delete')}>
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const deletingDataSource = _.find(dataSource, { id: record?.id })

                  ModalAntd.confirm({
                    title: 'Confirm deletion: Group Attribute',
                    icon: null,
                    maskClosable: true,
                    closeIcon: true,
                    content: (
                      <div>
                        <dl>
                          <dt>Group Attribute Name</dt>
                          <dd>- {deletingDataSource.name}</dd>
                          <dt>Group Attribute Description</dt>
                          <dd>- {deletingDataSource.description}</dd>
                        </dl>
                      </div>
                    ),
                    okButtonProps: {
                      shape: 'round',
                    },
                    okText: 'Delete',
                    okType: 'primary',
                    cancelButtonProps: {
                      shape: 'round',
                    },
                    cancelText: 'Cancel',
                    onOk: async () => {
                      await _delete(record?.id)
                    },
                  })
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-group-attribute'>
      <Page
        title={t('Group Attribute')}
        searchPlaceholder={t('Search Group Attribute or description')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between'>
            <Col></Col>
            <Col>
              <Button type='primary' size='large' onClick={() => setShowCreateModal(true)}>
                {t('Create New Group Attribute')}
              </Button>
            </Col>
          </Row>
          <Card title={t('Group Attribute')}>
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              sort
              loading={loading}
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              onSort={async ({ items }) => {
                await _reorder(items)
              }}
              scroll={{ x: 800 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={600}
        visible={showCreateModal}
        title={editingId ? t('Update Group Attribute') : t('Create New Group Attribute')}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form.resetFields()
          }

          setEditingId(null)
        }}
        footer={[
          <Button
            key='create'
            type='primary'
            onClick={() => {
              form.submit()
            }}
          >
            {editingId ? t('Update') : t('Create')}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              isEnabled: true,
            }}
            onFinish={(values) => {
              if (editingId) {
                _update(editingId, values)
              } else {
                _create(values)
              }
            }}
          >
            {editingId && (
              <Form.Item name='order' hidden>
                <Input />
              </Form.Item>
            )}

            <Form.Item label={t('Group Attribute Name')} name='name' rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item
              label={t('Group Attribute Description')}
              name='description'
              rules={[{ required: true }]}
            >
              <TextArea />
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Form.Item name='isEnabled' label='Status'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
              <Col></Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
